if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.slideTransitionMs = 280;
        this.slideCleanupTimeout = null;
        this.isSliding = false;
        this.swipeState = {
          tracking: false,
          startX: 0,
          startY: 0,
          deltaX: 0,
          deltaY: 0,
          identifier: null,
        };
        this.swipeThreshold = 48;
        this.swipeDirectionRatio = 1.2;

        this.handleTouchStart = this.handleTouchStart.bind(this);
        this.handleTouchMove = this.handleTouchMove.bind(this);
        this.handleTouchEnd = this.handleTouchEnd.bind(this);
        this.handleTouchCancel = this.handleTouchCancel.bind(this);

        const prevBtn = this.querySelector('.product-media-modal__nav--prev');
        const nextBtn = this.querySelector('.product-media-modal__nav--next');
        if (prevBtn) prevBtn.addEventListener('click', () => this.goToPrev());
        if (nextBtn) nextBtn.addEventListener('click', () => this.goToNext());
        const thumbnailsContainer = this.querySelector('.product-media-modal__thumbnails');
        if (thumbnailsContainer) {
          thumbnailsContainer.addEventListener('click', (e) => {
            const thumb = e.target.closest('.product-media-modal__thumb');
            if (!thumb) return;
            const mediaId = thumb.getAttribute('data-media-id');
            const slide = this.querySelector(`[role="document"] [data-media-id="${mediaId}"]`);
            if (!slide) return;

            const slides = this.getSlides();
            const currentIndex = slides.findIndex((el) => el.classList.contains('active'));
            const targetIndex = slides.indexOf(slide);
            const direction = targetIndex > currentIndex ? 1 : -1;
            this.setActiveSlide(slide, { direction });
          });
        }

        const mediaContainer = this.querySelector('[role="document"]');
        if (mediaContainer) {
          mediaContainer.addEventListener('touchstart', this.handleTouchStart, { passive: true });
          mediaContainer.addEventListener('touchmove', this.handleTouchMove, { passive: false });
          mediaContainer.addEventListener('touchend', this.handleTouchEnd, { passive: true });
          mediaContainer.addEventListener('touchcancel', this.handleTouchCancel, { passive: true });
        }
      }

      getSlides() {
        const container = this.querySelector('[role="document"]');
        return container ? Array.from(container.children) : [];
      }

      updateNavButtons() {
        const slides = this.getSlides();
        const currentIndex = slides.findIndex((el) => el.classList.contains('active'));
        const prevBtn = this.querySelector('.product-media-modal__nav--prev');
        const nextBtn = this.querySelector('.product-media-modal__nav--next');
        if (prevBtn) {
          prevBtn.disabled = currentIndex <= 0;
        }
        if (nextBtn) {
          nextBtn.disabled = currentIndex < 0 || currentIndex >= slides.length - 1;
        }
      }

      updateThumbnails() {
        const activeSlide = this.querySelector('[role="document"] .active[data-media-id]');
        const activeId = activeSlide ? activeSlide.getAttribute('data-media-id') : null;
        this.querySelectorAll('.product-media-modal__thumb').forEach((thumb) => {
          thumb.classList.toggle('is-active', thumb.getAttribute('data-media-id') === activeId);
        });
      }

      clearSlideClasses(slide) {
        if (!slide) return;
        slide.classList.remove(
          'is-entering',
          'is-leaving',
          'is-animating',
          'is-enter-from-right',
          'is-enter-from-left',
          'is-leave-to-left',
          'is-leave-to-right'
        );
      }

      resetSlideState() {
        const container = this.querySelector('[role="document"]');
        if (!container) return;

        container.classList.remove('is-sliding');
        container.querySelectorAll('[data-media-id]').forEach((slide) => this.clearSlideClasses(slide));
        this.isSliding = false;

        if (!this.slideCleanupTimeout) return;
        window.clearTimeout(this.slideCleanupTimeout);
        this.slideCleanupTimeout = null;
      }

      isMobileSwipeEnabled() {
        return (
          this.hasAttribute('open') &&
          window.matchMedia('(max-width: 749px)').matches &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
      }

      resetSwipeState() {
        this.swipeState.tracking = false;
        this.swipeState.startX = 0;
        this.swipeState.startY = 0;
        this.swipeState.deltaX = 0;
        this.swipeState.deltaY = 0;
        this.swipeState.identifier = null;
      }

      getTouchById(touchList, identifier) {
        for (const touch of touchList) {
          if (touch.identifier === identifier) return touch;
        }

        return null;
      }

      handleTouchStart(event) {
        if (!this.isMobileSwipeEnabled() || this.isSliding || event.touches.length !== 1) return;
        if (event.target.closest('video, iframe, model-viewer, button, a, input, textarea, select')) return;

        const touch = event.touches[0];
        this.swipeState.tracking = true;
        this.swipeState.startX = touch.clientX;
        this.swipeState.startY = touch.clientY;
        this.swipeState.deltaX = 0;
        this.swipeState.deltaY = 0;
        this.swipeState.identifier = touch.identifier;
      }

      handleTouchMove(event) {
        if (!this.swipeState.tracking) return;

        const touch = this.getTouchById(event.touches, this.swipeState.identifier);
        if (!touch) return;

        this.swipeState.deltaX = touch.clientX - this.swipeState.startX;
        this.swipeState.deltaY = touch.clientY - this.swipeState.startY;

        if (Math.abs(this.swipeState.deltaX) > Math.abs(this.swipeState.deltaY)) {
          event.preventDefault();
        }
      }

      handleTouchEnd(event) {
        if (!this.swipeState.tracking) return;

        const touch = this.getTouchById(event.changedTouches, this.swipeState.identifier);
        const deltaX = touch ? touch.clientX - this.swipeState.startX : this.swipeState.deltaX;
        const deltaY = touch ? touch.clientY - this.swipeState.startY : this.swipeState.deltaY;
        const absDeltaX = Math.abs(deltaX);
        const absDeltaY = Math.abs(deltaY);
        const isHorizontalSwipe =
          absDeltaX >= this.swipeThreshold && absDeltaX > absDeltaY * this.swipeDirectionRatio;

        this.resetSwipeState();
        if (!isHorizontalSwipe || this.isSliding) return;

        if (deltaX < 0) {
          this.goToNext();
          return;
        }

        this.goToPrev();
      }

      handleTouchCancel() {
        this.resetSwipeState();
      }

      setActiveSlide(activeMedia, options = {}) {
        const { direction = 0, animate = true } = options;
        const container = this.querySelector('[role="document"]');
        if (!container) return;

        const currentActiveMedia = container.querySelector('.active[data-media-id]');
        if (!activeMedia || currentActiveMedia === activeMedia) return;

        const loadActiveDeferredMedia = () => {
          const activeMediaTemplate = activeMedia.querySelector('template');
          const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
          if (
            activeMedia.nodeName === 'DEFERRED-MEDIA' &&
            activeMediaContent &&
            activeMediaContent.querySelector('.js-youtube')
          ) {
            activeMedia.loadContent();
            return;
          }
          if (activeMedia.nodeName === 'DEFERRED-MEDIA' && activeMedia.loadContent) {
            activeMedia.loadContent();
          }
        };

        const allowAnimatedSlide =
          animate &&
          !this.isSliding &&
          currentActiveMedia &&
          !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!allowAnimatedSlide) {
          this.resetSlideState();
          container.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('active'));
          activeMedia.classList.add('active');
          loadActiveDeferredMedia();
        } else {
          const slides = this.getSlides();
          const currentIndex = slides.indexOf(currentActiveMedia);
          const nextIndex = slides.indexOf(activeMedia);
          const slideDirection = direction || (nextIndex > currentIndex ? 1 : -1);
          const enteringClass = slideDirection > 0 ? 'is-enter-from-right' : 'is-enter-from-left';
          const leavingClass = slideDirection > 0 ? 'is-leave-to-left' : 'is-leave-to-right';

          this.clearSlideClasses(currentActiveMedia);
          this.clearSlideClasses(activeMedia);
          currentActiveMedia.classList.remove('active');
          currentActiveMedia.classList.add('is-leaving', leavingClass);
          activeMedia.classList.add('active', 'is-entering', enteringClass);
          container.classList.add('is-sliding');
          this.isSliding = true;

          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              currentActiveMedia.classList.add('is-animating');
              activeMedia.classList.add('is-animating');
            });
          });

          if (this.slideCleanupTimeout) {
            window.clearTimeout(this.slideCleanupTimeout);
          }

          this.slideCleanupTimeout = window.setTimeout(() => {
            this.clearSlideClasses(currentActiveMedia);
            this.clearSlideClasses(activeMedia);
            container.classList.remove('is-sliding');
            this.isSliding = false;
            this.slideCleanupTimeout = null;
            loadActiveDeferredMedia();
          }, this.slideTransitionMs + 80);
        }

        this.updateNavButtons();
        this.updateThumbnails();
      }

      goToPrev() {
        const slides = this.getSlides();
        const currentIndex = slides.findIndex((el) => el.classList.contains('active'));
        if (currentIndex <= 0) return;
        this.setActiveSlide(slides[currentIndex - 1], { direction: -1 });
      }

      goToNext() {
        const slides = this.getSlides();
        const currentIndex = slides.findIndex((el) => el.classList.contains('active'));
        if (currentIndex < 0 || currentIndex >= slides.length - 1) return;
        this.setActiveSlide(slides[currentIndex + 1], { direction: 1 });
      }

      hide() {
        this.resetSlideState();
        this.resetSwipeState();
        super.hide();
        if (!document.querySelector('product-modal[open]')) {
          document.body.classList.remove('product-media-modal-open');
        }
      }

      show(opener) {
        super.show(opener);
        document.body.classList.add('product-media-modal-open');
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.resetSlideState();
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(
          `[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`
        );
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView({ block: 'nearest', inline: 'nearest' });

        const container = this.querySelector('[role="document"]');
        if (container && activeMedia.width) {
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        } else if (activeMedia.nodeName === 'DEFERRED-MEDIA' && activeMedia.loadContent) {
          activeMedia.loadContent();
        }
        this.updateNavButtons();
        this.updateThumbnails();
      }
    }
  );
}
